import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Router, RouterLink } from '@angular/router';
import { APP_PATHS } from '@core/constants';
import { locationAddressLine } from '@core/helpers/address.helper';
import { canUseInstallationFeature } from '@core/helpers/installation-access.helper';
import { explainedInPlace, toRefusal } from '@core/helpers/installation-refusal.helper';
import { installerOidFor } from '@core/helpers/installer-oid.helper';
import { InstallationStatusEnum } from '@core/models/device.model';
import { Installation, Preset } from '@core/models/installation.model';
import { LocationFilterModel } from '@core/models/location.model';
import { NewInstallation } from '@core/models/installation-write.model';
import { DeviceService } from '@core/services';
import { InstallationProfileService } from '@core/services/installation-profile.service';
import { InstallationService } from '@core/services/installation.service';
import { LocationService } from '@core/services/location.service';
import {
  DialogShellComponent,
  FilledButtonComponent,
  SelectFieldComponent,
  TextButtonComponent,
  TextFieldComponent,
} from '@custom-components';
import { TranslateModule } from '@ngx-translate/core';
import { AuthStore } from '@root/app/store/auth.store';
import { UserStore } from '@root/app/store/user.store';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import {
  CreatedLocation,
  LocationDialogComponent,
} from '../../../locations/components/location-dialog/location-dialog.component';
import { createdLocationFilter, findCreatedLocation } from '../../created-location';
import { DetailsRefusal, detailsRefusal } from '../../details-refusal';
import { LinkNotice, linkNoticeState, linkedNotice, refusedNotice } from '../../link-notice';
import { linkRefusal } from '../../link-refusal';
import { linkResult } from '../../link-result';

export interface InstallationCreateDialogData {
  // Opened from a location: the location is fixed and not searched for.
  location?: LocationOption;
}

export interface LocationOption {
  locationId: number;
  label: string;
  // The partner whose devices can be linked, and on whose behalf a Xemex user links one.
  partnerOid: string | null;
}

export interface DeviceOption {
  deviceId: string;
  ldn: string;
}

export const LOCATION_SEARCH_DEBOUNCE_MS = 300;
const LOCATION_SEARCH_MIN_CHARS = 2;
const LOCATION_SEARCH_LIMIT = 10;
const DEVICE_SEARCH_LIMIT = 10;
export const NAME_MAX_LENGTH = 255;

export function installationCreateDialogConfig(
  data: InstallationCreateDialogData,
): MatDialogConfig<InstallationCreateDialogData> {
  return { width: '40rem', maxWidth: 'calc(100vw - 2rem)', maxHeight: 'calc(100vh - 2rem)', data };
}

// Closes with the installation it created, and opens its page; closes with nothing when cancelled.
@Component({
  selector: 'xemex-installation-create-dialog',
  imports: [
    DialogShellComponent,
    FilledButtonComponent,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    RouterLink,
    SelectFieldComponent,
    TextButtonComponent,
    TextFieldComponent,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './installation-create-dialog.component.html',
})
export class InstallationCreateDialogComponent {
  readonly data = inject<InstallationCreateDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<InstallationCreateDialogComponent, Installation>>(MatDialogRef);
  private readonly installationService = inject(InstallationService);
  private readonly profileService = inject(InstallationProfileService);
  private readonly locationService = inject(LocationService);
  private readonly deviceService = inject(DeviceService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly userStore = inject(UserStore);
  private readonly authStore = inject(AuthStore);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly APP_PATHS = APP_PATHS;
  protected readonly NAME_MAX_LENGTH = NAME_MAX_LENGTH;

  readonly locationControl = new FormControl<string | LocationOption>(
    this.data.location?.label ?? '',
    { nonNullable: true },
  );
  readonly deviceControl = new FormControl<string | DeviceOption>('', { nonNullable: true });
  readonly nameControl = new FormControl('', { nonNullable: true });
  readonly remarksControl = new FormControl('', { nonNullable: true });

  readonly selected = signal<LocationOption | null>(this.data.location ?? null);
  readonly options = signal<LocationOption[]>([]);
  readonly searching = signal(false);
  readonly searchFailed = signal(false);
  readonly searched = signal(false);
  readonly isSaving = signal(false);
  readonly refusal = signal<DetailsRefusal | null>(null);
  readonly presets = signal<Preset[]>([]);
  readonly presetsFailed = signal(false);
  readonly presetCode = signal('');
  readonly createdLocationMissing = signal(false);
  readonly selectedDevice = signal<DeviceOption | null>(null);
  readonly deviceOptions = signal<DeviceOption[]>([]);
  readonly deviceSearching = signal(false);
  readonly deviceSearchFailed = signal(false);
  readonly deviceSearched = signal(false);
  // What was typed in the device field, which is only a device once an option is picked.
  private readonly deviceQuery = signal('');

  private readonly name = toSignal(this.nameControl.valueChanges, { initialValue: '' });
  protected readonly nameTooLong = computed(() => this.name().trim().length > NAME_MAX_LENGTH);

  private readonly deviceTypedNotPicked = computed(
    () => this.deviceQuery().trim() !== '' && this.selectedDevice() === null,
  );

  readonly canSave = computed(
    () =>
      this.selected() !== null &&
      this.presetCode() !== '' &&
      !this.deviceTypedNotPicked() &&
      !this.nameTooLong() &&
      !this.isSaving(),
  );

  protected readonly canLinkDevice = computed(() =>
    canUseInstallationFeature(
      this.userStore.user(),
      this.authStore.permissions(),
      'provision_device',
    ),
  );

  protected readonly canAddLocation = computed(
    () =>
      !this.data.location &&
      canUseInstallationFeature(
        this.userStore.user(),
        this.authStore.permissions(),
        'add_new_location',
      ),
  );

  constructor() {
    this.loadPresets();
    this.resetDevice();
    this.searchDevices();
    if (this.data.location) return;
    this.locationControl.valueChanges
      .pipe(
        // Picking an option sets the control to the option itself; only typed text is a search.
        filter((value): value is string => typeof value === 'string'),
        tap(() => {
          this.selected.set(null);
          this.refusal.set(null);
          this.createdLocationMissing.set(false);
          this.resetDevice();
        }),
        debounceTime(LOCATION_SEARCH_DEBOUNCE_MS),
        map(query => query.trim()),
        distinctUntilChanged(),
        tap(query => {
          this.options.set([]);
          this.searchFailed.set(false);
          this.searched.set(false);
          this.searching.set(query.length >= LOCATION_SEARCH_MIN_CHARS);
        }),
        switchMap(query => this.search(query)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(options => {
        this.options.set(options);
        this.searching.set(false);
      });
  }

  protected displayLocation(value: string | LocationOption | null): string {
    return typeof value === 'string' ? value : (value?.label ?? '');
  }

  protected onLocationSelected(event: MatAutocompleteSelectedEvent): void {
    const option = event.option.value as LocationOption;
    this.select(option);
  }

  protected displayDevice(value: string | DeviceOption | null): string {
    return typeof value === 'string' ? value : (value?.ldn ?? '');
  }

  protected onDeviceSelected(event: MatAutocompleteSelectedEvent): void {
    const option = event.option.value as DeviceOption;
    this.selectedDevice.set(option);
    this.deviceQuery.set(option.ldn);
    this.deviceControl.setValue(option, { emitEvent: false });
  }

  protected addLocation(): void {
    this.dialog
      .open<LocationDialogComponent, { mode: 'create'; returnsCreated: true }, CreatedLocation>(
        LocationDialogComponent,
        {
          panelClass: 'no-padding-dialog',
          width: '44rem',
          maxWidth: 'calc(100vw - 2rem)',
          data: { mode: 'create', returnsCreated: true },
        },
      )
      .afterClosed()
      .pipe(
        filter((created): created is CreatedLocation => !!created),
        switchMap(created => this.findCreated(created)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(option => {
        if (option) this.select(option);
        else this.createdLocationMissing.set(true);
      });
  }

  save(): void {
    const location = this.selected();
    if (location === null || !this.canSave()) return;
    this.isSaving.set(true);
    this.refusal.set(null);
    this.dialogRef.disableClose = true;
    this.installationService
      .create(this.body(location.locationId))
      .pipe(
        switchMap(installation => this.linkDevice(installation, location)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ installation, notice }) => {
          this.dialogRef.close(installation);
          void this.router.navigate(
            ['/', APP_PATHS.Installations, installation.installationOid, 'general'],
            notice ? { state: linkNoticeState(notice) } : undefined,
          );
        },
        error: (error: unknown) => {
          this.isSaving.set(false);
          this.dialogRef.disableClose = false;
          this.refusal.set(detailsRefusal('create', toRefusal(error)));
        },
      });
  }

  cancel(): void {
    if (this.isSaving()) return;
    this.dialogRef.close();
  }

  private body(locationId: number): NewInstallation {
    const name = this.nameControl.value.trim();
    const remarks = this.remarksControl.value.trim();
    return {
      location_id: locationId,
      preset_code: this.presetCode(),
      ...(name ? { name } : {}),
      ...(remarks ? { remarks } : {}),
    };
  }

  private select(option: LocationOption): void {
    this.selected.set(option);
    this.refusal.set(null);
    this.createdLocationMissing.set(false);
    this.locationControl.setValue(option.label, { emitEvent: false });
    this.resetDevice();
  }

  // Once the installation exists the dialog goes on to its page whatever happens to the link, so
  // the link never fails the create: its outcome is handed to the page as a notice.
  private linkDevice(
    installation: Installation,
    location: LocationOption,
  ): Observable<{ installation: Installation; notice: LinkNotice | null }> {
    const device = this.selectedDevice();
    if (!device) return of({ installation, notice: null });
    const installerOid = installerOidFor(this.userStore.user(), location.partnerOid);
    if (!installerOid) {
      return of({ installation, notice: refusedNotice(linkRefusal({ status: 0, reason: null })) });
    }
    return this.deviceService
      .provisionDevice(
        device.deviceId,
        location.locationId,
        installerOid,
        undefined,
        installation.installationOid,
        explainedInPlace(),
      )
      .pipe(
        map(response => {
          const result = linkResult(response, device.deviceId);
          return { installation, notice: result ? linkedNotice(result) : null };
        }),
        catchError((error: unknown) =>
          of({ installation, notice: refusedNotice(linkRefusal(toRefusal(error))) }),
        ),
      );
  }

  // The device belongs to the location's partner, so it is picked again whenever the location
  // changes, and the field waits for a location whose partner is known.
  private resetDevice(): void {
    this.selectedDevice.set(null);
    this.deviceQuery.set('');
    this.deviceOptions.set([]);
    this.deviceSearching.set(false);
    this.deviceSearchFailed.set(false);
    this.deviceSearched.set(false);
    this.deviceControl.setValue('', { emitEvent: false });
    if (this.selected()?.partnerOid) this.deviceControl.enable({ emitEvent: false });
    else this.deviceControl.disable({ emitEvent: false });
  }

  private searchDevices(): void {
    this.deviceControl.valueChanges
      .pipe(
        filter((value): value is string => typeof value === 'string'),
        tap(query => {
          this.selectedDevice.set(null);
          this.deviceQuery.set(query);
        }),
        debounceTime(LOCATION_SEARCH_DEBOUNCE_MS),
        map(query => query.trim()),
        tap(query => {
          this.deviceOptions.set([]);
          this.deviceSearchFailed.set(false);
          this.deviceSearched.set(false);
          this.deviceSearching.set(query.length >= LOCATION_SEARCH_MIN_CHARS);
        }),
        switchMap(query => this.findDevices(query)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(options => {
        this.deviceOptions.set(options);
        this.deviceSearching.set(false);
      });
  }

  private findDevices(query: string): Observable<DeviceOption[]> {
    const partnerOid = this.selected()?.partnerOid;
    if (!partnerOid || query.length < LOCATION_SEARCH_MIN_CHARS) {
      return of<DeviceOption[]>([]);
    }
    return this.deviceService
      .getListDevicesFilter({
        partnerOid,
        provisioning: InstallationStatusEnum.Linked,
        deviceLDN: query,
        limit: DEVICE_SEARCH_LIMIT,
        offset: 0,
      })
      .pipe(
        map(response =>
          (response.devices ?? []).map(device => ({
            deviceId: device.deviceId,
            ldn: device.deviceLDN,
          })),
        ),
        tap(() => this.deviceSearched.set(true)),
        catchError(() => {
          this.deviceSearchFailed.set(true);
          return of<DeviceOption[]>([]);
        }),
      );
  }

  private findCreated(created: CreatedLocation): Observable<LocationOption | null> {
    return this.locationService.getListLocationsFilter(createdLocationFilter(created)).pipe(
      map(response => {
        const row = findCreatedLocation(response.locations ?? [], created);
        return row
          ? {
              locationId: row.locationId,
              label: locationAddressLine(row),
              partnerOid: row.partnerOid ?? null,
            }
          : null;
      }),
      catchError(() => of(null)),
    );
  }

  private loadPresets(): void {
    this.profileService
      .presets()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: presets => {
          this.presets.set(presets);
          const platformDefault = presets.find(preset => preset.isPlatformDefault);
          if (platformDefault) this.presetCode.set(platformDefault.preset);
        },
        error: () => this.presetsFailed.set(true),
      });
  }

  private search(query: string): Observable<LocationOption[]> {
    if (query.length < LOCATION_SEARCH_MIN_CHARS) return of<LocationOption[]>([]);
    const filter: LocationFilterModel = { limit: LOCATION_SEARCH_LIMIT, offset: 0, address: query };
    return this.locationService.getListLocationsFilter(filter).pipe(
      map(response =>
        (response.locations ?? []).map(location => ({
          locationId: location.locationId,
          label: locationAddressLine(location),
          partnerOid: location.partnerOid ?? null,
        })),
      ),
      tap(() => this.searched.set(true)),
      catchError(() => {
        this.searchFailed.set(true);
        return of<LocationOption[]>([]);
      }),
    );
  }
}
